import { types, Instance, SnapshotIn, SnapshotOut } from 'mobx-state-tree'
import { deleteCookie, API } from '../services/api'
import { IUser } from '../models/user_model'
import { clearLastWorkspaceSlug } from './WorkspaceStore'

// Auth flow status type
export type AuthStatus = 'idle' | 'otp_required' | 'blocked'

export const UserModel = types
  .model('UserStore')
  .props({
    id: types.maybe(types.string),
    email: types.maybe(types.string),
    first_name: types.maybe(types.string),
    last_name: types.maybe(types.string),
    avatar: types.maybeNull(types.string),
    avatar_id: types.maybeNull(types.string),
    role: types.maybe(
      types.union(types.literal(0), types.literal(1), types.literal(5), types.literal(10))
    ),
    color: types.maybe(types.string), // HEX color like "#3498db"
    is_men: types.maybe(types.boolean),
    can_set_app_cover: types.maybe(types.boolean),
    created_at: types.maybe(types.string),
    updated_at: types.maybe(types.string),
    last_action_at: types.maybe(types.string),
    password_change_required: types.maybe(types.boolean),
    location_sync_signature: types.maybe(types.string),
  })
  .volatile(() => ({
    // Auth flow state
    authStatus: 'idle' as AuthStatus,
    sessionId: undefined as string | undefined,
    deliveryMethod: undefined as 'email' | 'app' | undefined,
    pendingEmail: undefined as string | undefined,
  }))
  .actions((self) => ({
    logout() {
      // Invalidate refresh token on the server (before clearing credentials)
      const api = API.getInstance()
      api.logout()
      clearLastWorkspaceSlug()
      self.id = undefined
      self.email = undefined
      self.first_name = undefined
      self.last_name = undefined
      self.avatar = undefined
      self.avatar_id = undefined
      self.role = undefined
      self.color = undefined
      self.is_men = undefined
      self.can_set_app_cover = undefined
      self.created_at = undefined
      self.updated_at = undefined
      self.last_action_at = undefined
      self.password_change_required = undefined
      self.location_sync_signature = undefined
      // Reset auth flow state
      self.authStatus = 'idle'
      self.sessionId = undefined
      self.deliveryMethod = undefined
      self.pendingEmail = undefined
      deleteCookie('auth')
    },
    setUserData(data: IUser) {
      self.id = data.id
      self.email = data.email
      self.first_name = data.first_name
      self.last_name = data.last_name
      self.avatar = data.avatar
      self.avatar_id = data.avatar_id
      self.role = data.role
      self.color = data.color
      self.is_men = data.is_men
      self.can_set_app_cover = data.can_set_app_cover
      self.created_at = data.created_at
      self.updated_at = data.updated_at
      self.last_action_at = data.last_action_at
      self.password_change_required = data.password_change_required
      self.location_sync_signature = data.location_sync_signature
      // Reset auth flow state on successful login
      self.authStatus = 'idle'
      self.sessionId = undefined
      self.deliveryMethod = undefined
      self.pendingEmail = undefined
    },
    setOtpRequired(sessionId: string, deliveryMethod: 'email' | 'app', email: string) {
      self.authStatus = 'otp_required'
      self.sessionId = sessionId
      self.deliveryMethod = deliveryMethod
      self.pendingEmail = email
    },
    setUserBlocked(email: string) {
      self.authStatus = 'blocked'
      self.pendingEmail = email
    },
    resetAuthFlow() {
      self.authStatus = 'idle'
      self.sessionId = undefined
      self.deliveryMethod = undefined
      self.pendingEmail = undefined
    },
    isLogged() {
      return Boolean(self.id)
    },
  }))

export type UserStore = Instance<typeof UserModel>
export type IUserSnapshotIn = SnapshotIn<typeof UserModel>
export type IUserSnapshotOut = SnapshotOut<typeof UserModel>
